import { useState, useEffect, useCallback, useRef } from 'react';

interface VersionInfo {
  version: string;
  buildTime: string;
}

interface UseVersionCheckOptions {
  checkInterval?: number; // in milliseconds
  onUpdateAvailable?: () => void;
  autoUpdate?: boolean; // If true, automatically clear cache and reload
}

export const useVersionCheck = (options: UseVersionCheckOptions = {}) => {
  const {
    checkInterval = 5 * 60 * 1000, // Default: check every 5 minutes
    onUpdateAvailable,
    autoUpdate = false
  } = options;

  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [currentBuildTime, setCurrentBuildTime] = useState<string | null>(null);
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const isInitialCheck = useRef(true);

  const forceReload = useCallback(async () => {
    try {
      // Clear all browser caches (Cache Storage API)
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
      }

      // Unregister all service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((reg) => reg.unregister()));
      }
    } catch (error) {
      console.error('Error clearing cache:', error);
    }

    // Hard reload the page
    window.location.reload();
  }, []);

  const checkForUpdates = useCallback(async () => {
    // Skip version checks in development
    if (import.meta.env.MODE === 'development') {
      return;
    }

    try {
      setIsChecking(true);

      // Fetch meta.json with cache-busting query param
      const response = await fetch(`/meta.json?t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
        },
      });

      if (!response.ok) {
        // Silently fail - meta.json might not exist
        return;
      }

      const versionInfo: VersionInfo = await response.json();

      // On first load, just store the current version and build time
      if (isInitialCheck.current) {
        isInitialCheck.current = false;
        setCurrentVersion(versionInfo.version);
        setCurrentBuildTime(versionInfo.buildTime);
        console.log(`[Version] Current: ${versionInfo.version} (${versionInfo.buildTime})`);
        return;
      }

      // Check if build has changed (buildTime is unique per deploy)
      if (currentBuildTime && versionInfo.buildTime !== currentBuildTime) {
        console.log(`[Version] Update available: ${versionInfo.version} (build: ${versionInfo.buildTime}, was: ${currentVersion} build: ${currentBuildTime})`);
        setIsUpdateAvailable(true);
        onUpdateAvailable?.();

        // Auto-update if enabled
        if (autoUpdate) {
          console.log('[Version] Auto-updating...');
          await forceReload();
        }
      }
    } catch (error) {
      // Silently fail - don't spam console with errors
    } finally {
      setIsChecking(false);
    }
  }, [currentVersion, currentBuildTime, onUpdateAvailable, autoUpdate, forceReload]);

  useEffect(() => {
    // Check immediately on mount
    checkForUpdates();

    // Set up interval for periodic checks
    const interval = setInterval(checkForUpdates, checkInterval);

    // Also check when tab becomes visible (user returns to app)
    let lastFocusCheck = 0;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        // Throttle to max once per minute when refocusing
        if (now - lastFocusCheck > 60 * 1000) {
          lastFocusCheck = now;
          checkForUpdates();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkForUpdates, checkInterval]);

  return {
    currentVersion,
    isUpdateAvailable,
    isChecking,
    checkForUpdates,
    forceReload,
  };
};
